import { z } from "zod";
import type { ApiClient } from "../api/ApiClient";
import { ApiError } from "../api/errors";
import type { PushNotificationController, PushState } from "../pwa/pushNotifications";
import type { ListenerHandle } from "./NativePlatformServices";

/** Push channel of a native app (backend `PushChannel`). */
export type NativePushChannel = "Fcm" | "Apns";

/** Permission state reported by the push plugin. */
export type NativePushPermission = "prompt" | "prompt-with-rationale" | "granted" | "denied";

/** The part of the Capacitor `PushNotifications` plugin the controller uses (injected for tests). */
export interface PushPluginPort {
  checkPermissions(): Promise<{ readonly receive: NativePushPermission }>;
  requestPermissions(): Promise<{ readonly receive: NativePushPermission }>;
  register(): Promise<void>;
  unregister(): Promise<void>;
  addListener(
    eventName: "registration",
    listener: (token: { readonly value: string }) => void,
  ): Promise<ListenerHandle>;
  addListener(
    eventName: "registrationError",
    listener: (error: { readonly error: string }) => void,
  ): Promise<ListenerHandle>;
}

/** Server calls for device tokens (injected for tests). */
export interface NativePushApi {
  register(channel: NativePushChannel, token: string): Promise<void>;
  unregister(token: string): Promise<void>;
}

/** Storage of the token registered by this device (injected for tests). */
export interface NativePushTokenStore {
  read(): Promise<string | null>;
  write(token: string | null): Promise<void>;
}

/** Dependencies of {@link NativePushNotificationController}. */
export interface NativePushDependencies {
  /** Channel of the platform: FCM on Android, APNs on iOS. */
  readonly channel: NativePushChannel;
  /** Whether the app build is set up for push (`VITE_NATIVE_PUSH`); otherwise `notConfigured`. */
  readonly configured: boolean;
  readonly plugin: PushPluginPort;
  readonly api: NativePushApi;
  readonly tokens: NativePushTokenStore;
  /** Schedules the timeout of a token request; returns a cancel function. */
  readonly schedule: (callback: () => void, delayMs: number) => () => void;
  /** How long to wait for the token of the push service. */
  readonly tokenTimeoutMs?: number;
}

/** Raised when the push service did not issue a device token. */
export class PushRegistrationError extends Error {
  /**
   * Creates the error.
   *
   * @param reason - Short reason without personal data.
   */
  constructor(reason: string) {
    super(`Push registration failed: ${reason}`);
    this.name = "PushRegistrationError";
  }
}

const stateSchema = z.object({ registered: z.boolean() });

/** Default wait for the token of the push service. */
export const defaultTokenTimeoutMs = 20_000;

/**
 * Creates the server calls on top of the API client.
 *
 * @param client - The API client.
 * @returns The API.
 */
export function createHttpNativePushApi(client: ApiClient): NativePushApi {
  const api: NativePushApi = {
    register: async (channel, token): Promise<void> => {
      await client.post("/api/v1/push-subscriptions/native", { channel, token }, stateSchema);
    },
    unregister: async (token): Promise<void> => {
      await client.post("/api/v1/push-subscriptions:unregister", { token }, stateSchema);
    },
  };

  return api;
}

/**
 * Push notifications of the native apps (T-081): permission of the operating system, device token
 * of FCM (Android) or APNs (iOS), registration at the server. Opt-in per device like the PWA; the
 * token is registered again on every sign-in because the push services rotate tokens.
 */
export class NativePushNotificationController implements PushNotificationController {
  private readonly dependencies: NativePushDependencies;

  /**
   * Creates the controller.
   *
   * @param dependencies - Plugin, server calls, token storage and scheduler.
   */
  constructor(dependencies: NativePushDependencies) {
    this.dependencies = dependencies;
  }

  /** @inheritdoc */
  async getState(): Promise<PushState> {
    if (!this.dependencies.configured) {
      return "notConfigured";
    }

    const permission = (await this.dependencies.plugin.checkPermissions()).receive;
    const token = await this.dependencies.tokens.read();
    let state: PushState = "off";

    if (permission === "denied") {
      state = "denied";
    } else if (permission === "granted" && token !== null) {
      state = "on";
    }

    return state;
  }

  /** @inheritdoc */
  async enable(): Promise<PushState> {
    if (!this.dependencies.configured) {
      return "notConfigured";
    }

    const permission = (await this.dependencies.plugin.requestPermissions()).receive;

    if (permission !== "granted") {
      const refused: PushState = permission === "denied" ? "denied" : "off";
      return refused;
    }

    let token: string;

    try {
      token = await this.obtainToken();
    } catch (error) {
      if (error instanceof PushRegistrationError) {
        // No push service on this device (for example Android without Google services).
        return "unsupported";
      }

      throw error;
    }

    try {
      await this.dependencies.api.register(this.dependencies.channel, token);
    } catch (error) {
      if (error instanceof ApiError && error.code === "TT-3012") {
        await this.dependencies.plugin.unregister();
        return "notConfigured";
      }

      throw error;
    }

    await this.dependencies.tokens.write(token);
    const state: PushState = "on";

    return state;
  }

  /** @inheritdoc */
  async disable(): Promise<PushState> {
    if (!this.dependencies.configured) {
      return "notConfigured";
    }

    const token = await this.dependencies.tokens.read();

    if (token !== null) {
      await this.dependencies.api.unregister(token);
    }

    await this.dependencies.plugin.unregister();
    await this.dependencies.tokens.write(null);
    const state: PushState = "off";

    return state;
  }

  /** @inheritdoc */
  async refresh(): Promise<void> {
    if (!this.dependencies.configured) {
      return;
    }

    const permission = (await this.dependencies.plugin.checkPermissions()).receive;
    const stored = await this.dependencies.tokens.read();

    if (permission !== "granted" || stored === null) {
      return;
    }

    const token = await this.obtainToken();
    await this.dependencies.api.register(this.dependencies.channel, token);

    if (token !== stored) {
      // The old token is invalid at the push service; the server removes it on the next send.
      await this.dependencies.tokens.write(token);
    }
  }

  /** @inheritdoc */
  async removeBeforeSignOut(): Promise<void> {
    try {
      await this.disable();
    } catch {
      // Offline or session already gone: the server removes the device once the push service
      // reports the token as unknown; the next person on this device registers it anew.
    }
  }

  /**
   * Registers at the push service and waits for the device token.
   *
   * @returns The token.
   * @throws PushRegistrationError on a registration error or timeout.
   */
  private obtainToken(): Promise<string> {
    const { plugin, schedule } = this.dependencies;
    const timeoutMs = this.dependencies.tokenTimeoutMs ?? defaultTokenTimeoutMs;
    const handles: Promise<ListenerHandle>[] = [];
    const result = new Promise<string>((resolve, reject) => {
      const cancelTimeout = schedule(() => {
        reject(new PushRegistrationError("timeout"));
      }, timeoutMs);
      handles.push(
        plugin.addListener("registration", (token) => {
          cancelTimeout();
          resolve(token.value);
        }),
        plugin.addListener("registrationError", () => {
          cancelTimeout();
          reject(new PushRegistrationError("rejected by the push service"));
        }),
      );
      plugin.register().catch((error: unknown) => {
        cancelTimeout();
        reject(new PushRegistrationError(error instanceof Error ? error.name : "unavailable"));
      });
    });
    const token = result.finally(async () => {
      const registered = await Promise.all(handles);
      await Promise.all(registered.map((handle) => handle.remove()));
    });

    return token;
  }
}
